import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Icon, type IconName } from "@/components/ui/Icon";
import { api } from "@/lib/api";
import { t } from "@/lib/strings";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ResultKind = "workspace" | "board" | "row" | "comment" | "series" | "memo";

interface FlatResult {
  kind: ResultKind;
  id: string;
  label: string;
  subLabel?: string;
  tags?: string[];
  target: () => void;
}

export function CommandPalette({ open, onOpenChange }: Props) {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Debounce typing
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQ(q.trim()), 200);
    return () => window.clearTimeout(id);
  }, [q]);

  // Reset state when closed
  useEffect(() => {
    if (!open) {
      setQ("");
      setDebouncedQ("");
      setActiveIdx(0);
      return;
    }
    // Autofocus on open
    const id = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  const enabled = open && debouncedQ.length >= 2;
  const search = useQuery({
    queryKey: ["search", debouncedQ],
    queryFn: () => api.search(debouncedQ, 10),
    enabled,
    staleTime: 10_000,
  });

  const flatResults: FlatResult[] = useMemo(() => {
    const out: FlatResult[] = [];
    if (!search.data) return out;
    for (const w of search.data.workspaces) {
      out.push({
        kind: "workspace",
        id: `ws-${w.id}`,
        label: w.name,
        target: () => navigate({ to: "/w/$workspaceId", params: { workspaceId: w.id } }),
      });
    }
    for (const b of search.data.boards) {
      out.push({
        kind: "board",
        id: `bd-${b.id}`,
        label: b.name,
        target: () => navigate({ to: "/b/$boardId", params: { boardId: b.id } }),
      });
    }
    for (const r of search.data.rows) {
      out.push({
        kind: "row",
        id: `rw-${r.id}`,
        label: r.title || "—",
        subLabel: r.board_name,
        target: () =>
          navigate({
            to: "/b/$boardId",
            params: { boardId: r.board_id },
            search: { row: r.id } as Record<string, string>,
          }),
      });
    }
    for (const c of search.data.comments) {
      out.push({
        kind: "comment",
        id: `cm-${c.comment_id}`,
        label: c.excerpt || "—",
        subLabel: [c.board_name, c.row_title].filter(Boolean).join(" · "),
        target: () =>
          navigate({
            to: "/b/$boardId",
            params: { boardId: c.board_id },
            search: { row: c.row_id, comment: c.comment_id, q: debouncedQ } as Record<
              string,
              string
            >,
          }),
      });
    }
    for (const s of search.data.series) {
      out.push({
        kind: "series",
        id: `se-${s.series_id}`,
        label: s.name,
        subLabel: t.paletteSeriesTag,
        target: () => navigate({ to: "/meetings/$seriesId", params: { seriesId: s.series_id } }),
      });
    }
    for (const m of search.data.memos) {
      const breadcrumb = [m.series_name, isoToDe(m.meeting_date), m.section_title]
        .filter(Boolean)
        .join(" · ");
      const tags: string[] = [];
      if (m.retired) tags.push(t.paletteMemoRetired);
      if (m.more_count > 0) tags.push(`+${m.more_count} ${t.paletteMemoMore}`);
      out.push({
        kind: "memo",
        id: `me-${m.memo_id}-${m.entry_id ?? m.block_id ?? ""}`,
        label: m.excerpt || m.series_name,
        subLabel: breadcrumb,
        tags,
        target: () =>
          navigate({
            to: "/memo/$memoId",
            params: { memoId: m.memo_id },
            search: {
              ...(m.entry_id ? { entry: m.entry_id } : m.block_id ? { block: m.block_id } : {}),
              q: debouncedQ,
            } as Record<string, string>,
          }),
      });
    }
    return out;
  }, [search.data, navigate, debouncedQ]);

  // Clamp activeIdx to result list size
  useEffect(() => {
    if (activeIdx >= flatResults.length) {
      setActiveIdx(Math.max(0, flatResults.length - 1));
    }
  }, [flatResults.length, activeIdx]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, Math.max(0, flatResults.length - 1)));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        const pick = flatResults[activeIdx];
        if (pick) {
          e.preventDefault();
          pick.target();
          onOpenChange(false);
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        onOpenChange(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, flatResults, activeIdx, onOpenChange]);

  if (!open) return null;

  const grouped = search.data
    ? {
        workspace: search.data.workspaces.length,
        board: search.data.boards.length,
        row: search.data.rows.length,
        comment: search.data.comments.length,
        series: search.data.series.length,
        memo: search.data.memos.length,
      }
    : { workspace: 0, board: 0, row: 0, comment: 0, series: 0, memo: 0 };

  return (
    <div style={overlayStyle} onClick={() => onOpenChange(false)}>
      <div
        style={paletteStyle}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={t.paletteTitle}
      >
        <div style={inputRowStyle}>
          <Icon name="search" size={16} color="var(--ink-3)" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActiveIdx(0);
            }}
            placeholder={t.paletteInputPlaceholder}
            style={inputStyle}
          />
          <kbd style={kbdStyle}>Esc</kbd>
        </div>

        <div style={bodyStyle}>
          {debouncedQ.length < 2 ? (
            <Hint>{t.paletteMinChars}</Hint>
          ) : search.isLoading ? (
            <Hint>{t.paletteSearching}</Hint>
          ) : flatResults.length === 0 ? (
            <Hint>{t.paletteNoResults}</Hint>
          ) : (
            <>
              {renderGroup(
                t.paletteGroupWorkspaces,
                flatResults.slice(0, grouped.workspace),
                0,
                activeIdx,
                setActiveIdx,
                onOpenChange,
              )}
              {renderGroup(
                t.paletteGroupBoards,
                flatResults.slice(grouped.workspace, grouped.workspace + grouped.board),
                grouped.workspace,
                activeIdx,
                setActiveIdx,
                onOpenChange,
              )}
              {renderGroup(
                t.paletteGroupRows,
                flatResults.slice(
                  grouped.workspace + grouped.board,
                  grouped.workspace + grouped.board + grouped.row,
                ),
                grouped.workspace + grouped.board,
                activeIdx,
                setActiveIdx,
                onOpenChange,
              )}
              {renderGroup(
                t.paletteGroupComments,
                flatResults.slice(
                  grouped.workspace + grouped.board + grouped.row,
                  grouped.workspace + grouped.board + grouped.row + grouped.comment,
                ),
                grouped.workspace + grouped.board + grouped.row,
                activeIdx,
                setActiveIdx,
                onOpenChange,
                debouncedQ,
              )}
              {renderGroup(
                t.paletteGroupMemos,
                flatResults.slice(
                  grouped.workspace + grouped.board + grouped.row + grouped.comment,
                ),
                grouped.workspace + grouped.board + grouped.row + grouped.comment,
                activeIdx,
                setActiveIdx,
                onOpenChange,
                debouncedQ,
              )}
            </>
          )}
        </div>

        <div style={footerStyle}>
          <FooterKbd>↑↓</FooterKbd> {t.paletteNav} &nbsp;&nbsp;
          <FooterKbd>↵</FooterKbd> {t.paletteOpen} &nbsp;&nbsp;
          <FooterKbd>Esc</FooterKbd> {t.paletteClose}
        </div>
      </div>
    </div>
  );
}

function renderGroup(
  label: string,
  rows: FlatResult[],
  baseIdx: number,
  activeIdx: number,
  setActiveIdx: (i: number) => void,
  onOpenChange: (open: boolean) => void,
  highlight = "",
) {
  if (rows.length === 0) return null;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={groupLabelStyle}>{label}</div>
      {rows.map((r, i) => {
        const idx = baseIdx + i;
        const isActive = idx === activeIdx;
        return (
          <button
            key={r.id}
            type="button"
            onMouseEnter={() => setActiveIdx(idx)}
            onClick={() => {
              r.target();
              onOpenChange(false);
            }}
            style={{
              ...rowStyle,
              background: isActive ? "var(--surface-soft)" : "transparent",
            }}
          >
            <Icon name={iconForKind(r.kind)} size={14} color="var(--ink-3)" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13.5,
                  color: "var(--ink-1)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {r.kind === "memo" || r.kind === "comment"
                  ? highlightMatch(r.label, highlight)
                  : r.label}
              </div>
              {r.subLabel ? (
                <div style={{ display: "flex", alignItems: "center", fontSize: 11.5 }}>
                  <span
                    style={{
                      color: "var(--ink-3)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                    }}
                  >
                    {r.subLabel}
                  </span>
                  {r.tags?.map((tag) => (
                    <span key={tag} style={memoTagStyle}>
                      {tag}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
            <Icon name="chevRight" size={12} color="var(--ink-4)" />
          </button>
        );
      })}
    </div>
  );
}

function iconForKind(kind: ResultKind): IconName {
  if (kind === "workspace") return "layers";
  if (kind === "board") return "checkbox";
  if (kind === "series" || kind === "memo") return "notebook-text";
  if (kind === "comment") return "comment";
  return "text";
}

function isoToDe(iso: string): string {
  const parts = iso.split("-");
  return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : iso;
}

function highlightMatch(text: string, q: string): React.ReactNode {
  if (!q) return text;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <span style={{ color: "var(--ink-1)", fontWeight: 600, background: "rgba(0,89,155,0.12)" }}>
        {text.slice(i, i + q.length)}
      </span>
      {text.slice(i + q.length)}
    </>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: "20px 16px", fontSize: 13, color: "var(--ink-3)", textAlign: "center" }}>
      {children}
    </div>
  );
}

function FooterKbd({ children }: { children: React.ReactNode }) {
  return <kbd style={footerKbdStyle}>{children}</kbd>;
}

const overlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(13,62,99,0.32)",
  backdropFilter: "blur(2px)",
  zIndex: 400,
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  paddingTop: "10vh",
  animation: "flow-fade-in 120ms ease-out",
};

const paletteStyle: CSSProperties = {
  width: "min(640px, calc(100vw - 32px))",
  maxHeight: "70vh",
  background: "var(--surface-elev)",
  borderRadius: 12,
  border: "1px solid var(--hairline-strong)",
  boxShadow: "0 20px 60px rgba(13,62,99,0.18), 0 4px 12px rgba(13,62,99,0.08)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
};

const inputRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "12px 14px",
  borderBottom: "1px solid var(--hairline)",
};

const inputStyle: CSSProperties = {
  flex: 1,
  border: "none",
  outline: "none",
  background: "transparent",
  fontSize: 15,
  color: "var(--ink-1)",
  fontFamily: "inherit",
};

const kbdStyle: CSSProperties = {
  fontFamily: "JetBrains Mono Variable, JetBrains Mono, ui-monospace, monospace",
  fontSize: 11,
  padding: "2px 6px",
  background: "var(--surface-soft)",
  color: "var(--ink-3)",
  borderRadius: 4,
  border: "1px solid var(--hairline)",
};

const bodyStyle: CSSProperties = {
  flex: 1,
  overflow: "auto",
  padding: "8px 6px",
  minHeight: 80,
};

const groupLabelStyle: CSSProperties = {
  padding: "8px 10px 4px",
  fontSize: 10.5,
  fontWeight: 600,
  color: "var(--ink-3)",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

const memoTagStyle: CSSProperties = {
  marginLeft: 6,
  fontSize: 10,
  color: "var(--ink-4)",
  fontWeight: 500,
  flexShrink: 0,
  whiteSpace: "nowrap",
};

const rowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  width: "100%",
  padding: "8px 10px",
  border: "none",
  borderRadius: 6,
  cursor: "pointer",
  fontFamily: "inherit",
  textAlign: "left",
  transition: "background .08s",
};

const footerStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  padding: "8px 14px",
  borderTop: "1px solid var(--hairline)",
  fontSize: 11,
  color: "var(--ink-4)",
};

const footerKbdStyle: CSSProperties = {
  fontFamily: "JetBrains Mono Variable, JetBrains Mono, ui-monospace, monospace",
  fontSize: 10.5,
  padding: "1px 5px",
  background: "var(--surface-soft)",
  color: "var(--ink-3)",
  borderRadius: 3,
  border: "1px solid var(--hairline)",
  marginRight: 4,
};
